import Debug from 'debug';
import struct from "python-struct";

import { BlHostError, ErrorCode } from './BlHostError';
import BlHostAbstractTransport from './BlHostAbstractTransport';

const debug = Debug('blhost:hid');

const LPC_BL_CMD_OUT_REPORT_ID = 0x1
const LPC_BL_DATA_OUT_REPORT_ID = 0x2
const LPC_BL_CMD_IN_REPORT_ID = 0x3
const LPC_BL_DATA_IN_REPORT_ID = 0x4


/**
 * USB HID BLHOST transport.
 */
export default class BlHostTransportHid extends BlHostAbstractTransport {
    // Creates the transport. A vid and pid (integer) is required,
    // and packetReceiptNotification may also be provided (defaults
    // to 16).
    constructor(webHidDevice, packetReceiveNotification = 16) {
        super(null, packetReceiveNotification);

        this.dev = webHidDevice;
        this.opened = false;
        this.mtu = 52; // NOTE: MUST be 4-byte aligned or flash writes will crash! // LPC HID max size = 60. 4 byte reserved for ReportID + length header
    }

    hidAvailable() {
        const available = "hid" in navigator;
        if (!available) {
            console.error('Sorry the WebHID API is not supported in your browser.<br>' +
                'Try enabling <span>Experimental Web Platform features</span> in <code>chrome://flags<code>.');
        }
        return available;
    }


    // Given a Uint8Array, sends it as the main payload / "data object".
    // Returns a Promise that resolves when payload has been transferred
    // and target has disconnected.
    sendFirmwareImage(bytes) {
        return super.sendFirmwareImage(bytes)
            .then(() => this.waitForDisconnect());
    }

    // Given a hidHeader and payload, sends it over HID, without SLIP encoding
    // This ensures that the serial port is open by calling this.open() - the first
    // call to writeRaw will actually open the port.
    writeReport(hidHeader, payload) {
        return this.open().then(() => (
            new Promise((res, rej) => {
                let written = 0;
                debug(' send --> [',
                    Buffer.from(hidHeader).toString('hex'), '] ',
                    Buffer.from(payload).toString('hex'));

                // WebHID specific:
                if (this.dev.write) {
                    let fullPayloadWithReportId = new Uint8Array(hidHeader.length + payload.length);
                    fullPayloadWithReportId.set(hidHeader, 0);
                    fullPayloadWithReportId.set(payload, hidHeader.length);
                    written = this.dev.write(fullPayloadWithReportId);
                }

                // Node-HID specific:
                if (this.dev.sendReport) {
                    let fullPayloadWithoutReportId = new Uint8Array(hidHeader.length - 1 + payload.length);
                    fullPayloadWithoutReportId.set(hidHeader.subarray(1), 0);
                    fullPayloadWithoutReportId.set(payload, hidHeader.length - 1);
                    written = this.dev.sendReport(hidHeader[0], fullPayloadWithoutReportId);
                }

                if (written) {
                    res();
                } else {
                    rej();
                }
            })
        ));
    }

    // Opens the port, if needed
    // Returns a Promise when opening is done.
    open() {
        if (!this.dev) {
            return Promise.reject();
        }

        if (this.opened) {
            return Promise.resolve();
        }

        // NODE-HID specific:
        if (this.dev.getDeviceInfo) {
            const info = this.dev.getDeviceInfo();
            console.log("Opening WebHID device: " + JSON.stringify(info));
        } else {
            console.log("Opening WebHID device: " + this.dev.vendorId + ":" + this.dev.productId + " " + this.dev.productName);
        }

        return new Promise(async (res, rej) => {
            // Open device (not needed for Node-HID)
            if (this.dev.open) {
                try {
                    await this.dev.open();
                    this.opened = true;
                } catch (e) {
                    console.log("Couldn't open WebHID device: " + e);
                    return rej(e);
                }
            } else {
                // For NodeHID, consider the device opened
                this.opened = true;
            }

            // Bind RX
            if (this.dev.on) {
                // NODE-HID specific
                this.dev.on('data', this.onData.bind(this));
            } else {
                this.dev.addEventListener("inputreport", (event) => {
                    const { data, device, reportId } = event;

                    // Handle only the ULX device and a specific report ID.
                    // XXX FIXME: if (device.productId !== 0x2007 && reportId !== 0x3f) return;

                    // add reportId as data[0] + append data array
                    let header = new Uint8Array(1);
                    header[0] = reportId;
                    let full_data = new Uint8Array(data.buffer.byteLength + header.length);
                    full_data.set(header, 0);
                    full_data.set(new Uint8Array(data.buffer), header.length);

                    this.onData(full_data);
                });
            }

            return res();
        });
    }

    // Waits for the target to disconnect. Times out if not
    // disconnected after 5000 ms.
    waitForDisconnect() {
        if (!this.dev || !this.opened) {
            debug('dev is already closed.');
            return Promise.resolve();
        }

        // Explicit close in all cases
        return new Promise(resolve => {
            this.dev.close(() => {
                this.opened = false;
                // resolve() must be delayed so macOS doesn't immediately
                // open the same device again which occurs when it is plugged in a hub,
                // and win7-x64 might also have the chance to clean up.
                setTimeout(resolve, 2000);
            });
        });
    }

    parse(data) {
        // Check if there's any data
        if (!data || data.length < 4) {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_ASSERT_EMPTY_RESPONSE));
        }

        // Check padding
        if (data[1] != 0) {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_UNEXPECTED_BYTES));
        }

        // Check HID length and trim to reported length (bootloader pads with more HID bytes, but they are garbage)
        const length = data[3] << 8 | data[2];
        if (data.length - 4 < length) {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_INSUFFICIENT_BYTES));
        }

        // strip off the HID header, and trim to reported length
        const response = data.subarray(4, 4 + length);

        debug(' recv <-- [',
            Buffer.from(data.subarray(0, 4)).toString('hex'), '] ',
            Buffer.from(response).toString('hex'));

        // Check reportId
        const reportId = data[0];

        if (reportId == LPC_BL_CMD_IN_REPORT_ID) {
            // Cmd/Resp data
            return super.parse(response);
        } else if (reportId == LPC_BL_DATA_IN_REPORT_ID) {
            // Raw data
            return Promise.resolve([LPC_BL_DATA_IN_REPORT_ID, response]);
        } else {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES));
        }
    }

    sendReport(reportId = LPC_BL_CMD_OUT_REPORT_ID, payload) {
        const hidHeader = this.createHidHeader(reportId, payload.length);
        return this.writeReport(hidHeader, payload);
    }

    createHidHeader(reportId, payloadLength) {
        var hidHeader = new Uint8Array(4);
        hidHeader[0] = reportId;
        hidHeader[1] = 0x0; // padding
        const lengthPacked = struct.pack('<H', [payloadLength]);
        hidHeader.set(lengthPacked, 2);
        return hidHeader;
    }

    sendCommand(commandId, paramList32bit) {
        const paramCount = paramList32bit.length;
        const cmdHeader = struct.pack('bbbb', [commandId, 0, 0, paramCount])

        var payload = new Uint8Array(4 + 4 * paramCount);
        payload.set(cmdHeader, 0);
        payload.set(new Uint8Array(paramList32bit.buffer), 4);

        return this.sendReport(LPC_BL_CMD_OUT_REPORT_ID, payload);
    }

    sendData(payload) {
        return this.sendReport(LPC_BL_DATA_OUT_REPORT_ID, payload);
    }
}
